import React, { useEffect, useState } from 'react';
import { detectLanguage } from '../translations';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { getTranslation } from '../translations';

const CookiePolicy: React.FC = () => {
  const [lang, setLang] = useState('en');
  const [t, setT] = useState(getTranslation('en'));

  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    const detectedLang = savedLang || detectLanguage();
    setLang(detectedLang);
    setT(getTranslation(detectedLang));
  }, []);

  const handleLanguageChange = (newLang: string) => {
    setLang(newLang);
    setT(getTranslation(newLang));
    localStorage.setItem('preferredLanguage', newLang);
  };

  const isKorean = lang === 'ko';

  return (
    <div className="font-sans antialiased bg-gray-50">
      <Navigation t={t} currentLang={lang} onLanguageChange={handleLanguageChange} />
      
      <div className="max-w-4xl mx-auto px-4 py-24 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          {isKorean ? '쿠키 정책' : 'Cookie Policy'}
        </h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-6">
            {isKorean 
              ? '최종 업데이트: 2025년 8월 1일'
              : 'Last Updated: August 1, 2025'}
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '1. 쿠키란 무엇인가?' : '1. What Are Cookies?'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '쿠키는 웹사이트를 방문할 때 귀하의 브라우저에 저장되는 작은 텍스트 파일입니다. 이는 웹사이트가 귀하의 방문을 기억하고 시간이 지남에 따라 귀하의 경험을 개선하는 데 도움이 됩니다.'
                : 'Cookies are small text files stored on your browser when you visit a website. They help the website remember your visit and improve your experience over time.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '2. 우리가 사용하는 쿠키 유형' : '2. Types of Cookies We Use'}
            </h2>
            
            <h3 className="text-xl font-semibold mb-3">
              {isKorean ? '2.1 필수 쿠키' : '2.1 Essential Cookies'}
            </h3>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '서비스의 기본 기능을 활성화하는 데 필요합니다. 이러한 쿠키 없이는 로그인, 계정 관리 등의 서비스를 제공할 수 없습니다.'
                : 'Required to enable basic features of the service. Without these cookies, services like login and account management cannot be provided.'}
            </p>

            <h3 className="text-xl font-semibold mb-3">
              {isKorean ? '2.2 기능 쿠키' : '2.2 Functional Cookies'}
            </h3>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '언어 설정, 사용자 선호도 등을 기억하여 개인화된 경험을 제공합니다.'
                : 'Remember language settings, user preferences, and provide a personalized experience.'}
            </p>

            <h3 className="text-xl font-semibold mb-3">
              {isKorean ? '2.3 분석 쿠키' : '2.3 Analytics Cookies'}
            </h3>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '방문자가 웹사이트를 어떻게 사용하는지 이해하고 서비스를 개선하는 데 도움이 됩니다.'
                : 'Help us understand how visitors use the website and improve our services.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '3. 쿠키 관리 방법' : '3. How to Manage Cookies'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '대부분의 웹 브라우저는 자동으로 쿠키를 수락하지만, 원하는 경우 브라우저 설정을 통해 쿠키를 거부하거나 삭제할 수 있습니다:'
                : 'Most web browsers automatically accept cookies, but you can refuse or delete cookies through your browser settings if you prefer:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Chrome: {isKorean ? '설정 > 개인정보 및 보안 > 쿠키' : 'Settings > Privacy and Security > Cookies'}</li>
              <li>Safari: {isKorean ? '환경설정 > 개인정보 보호' : 'Preferences > Privacy'}</li>
              <li>Firefox: {isKorean ? '설정 > 개인정보 및 보안' : 'Settings > Privacy & Security'}</li>
              <li>Edge: {isKorean ? '설정 > 쿠키 및 사이트 권한' : 'Settings > Cookies and Site Permissions'}</li>
            </ul>
            <p className="text-gray-700 mt-4">
              <strong>{isKorean ? '참고:' : 'Note:'}</strong> {isKorean
                ? '쿠키를 비활성화하면 일부 기능이 제대로 작동하지 않을 수 있습니다.'
                : 'Disabling cookies may affect some features and functionality.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '4. 제3자 쿠키' : '4. Third-Party Cookies'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '우리는 다음과 같은 신뢰할 수 있는 제3자 서비스를 사용합니다:'
                : 'We use the following trusted third-party services:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                <strong>Google Analytics:</strong> {isKorean ? '웹사이트 사용 분석' : 'Website usage analysis'}
              </li>
              <li>
                <strong>LemonSqueezy:</strong> {isKorean ? '결제 처리 및 구독 관리' : 'Payment processing and subscription management'}
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '5. 쿠키 정책 변경' : '5. Changes to Cookie Policy'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '이 쿠키 정책은 수시로 업데이트될 수 있습니다. 변경 사항은 이 페이지에 게시됩니다.'
                : 'This cookie policy may be updated from time to time. Changes will be posted on this page.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '6. 연락처' : '6. Contact Us'}
            </h2>
            <p className="text-gray-700">
              {isKorean
                ? '쿠키 정책에 대한 질문이 있으시면 다음으로 연락하세요:'
                : 'If you have questions about our cookie policy, please contact us at:'}
            </p>
            <p className="text-gray-700 mt-4">
              Groove Inc.<br />
              Email: privacy@aivuebiz.com
            </p>
          </section>
        </div>
      </div>
      
      <Footer t={t} />
    </div>
  );
};

export default CookiePolicy;